// Series List Table Component
// Display all series with key metrics in a comprehensive table

import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  IconButton,
  Tooltip,
} from '@mui/material';
import { Visibility, Delete } from '@mui/icons-material';
import { Series } from '../../types';
import { formatCurrency, formatPercentage } from '../../utils/navCalculations';
import { format } from 'date-fns';

interface SeriesListTableProps {
  series: Series[];
  lenders: any[]; // Will type properly later
  onViewDetails?: (series: Series) => void;
  onDelete?: (seriesId: string, seriesName: string) => void;
}

const SeriesListTable: React.FC<SeriesListTableProps> = ({
  series,
  lenders,
  onViewDetails,
  onDelete,
}) => {
  const activeSeries = series.filter(s => s.status === 'active');

  const getLenderName = (lenderId: string): string => {
    const lender = lenders.find(l => l.id === lenderId);
    return lender?.fullName || 'Unknown';
  };

  const getStatusChip = (status: string) => {
    const statusColors: Record<string, string> = {
      active: '#4CAF50',
      closed: '#999',
      redeemed: '#2196F3',
    };

    return (
      <Chip
        label={status.toUpperCase()}
        size="small"
        sx={{
          bgcolor: statusColors[status] || '#666',
          color: '#000',
          fontWeight: 600,
          fontSize: '0.75rem',
        }}
      />
    );
  };

  const getLockupStatus = (series: Series) => {
    const now = new Date();
    const lockupEnd = new Date(series.lockupEnd);
    const isLocked = now < lockupEnd;

    if (isLocked) {
      const daysRemaining = Math.ceil((lockupEnd.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
      return (
        <Chip
          label={`🔒 ${daysRemaining}d`}
          size="small"
          sx={{ bgcolor: '#FF9800', color: '#000', fontSize: '0.75rem' }}
        />
      );
    }

    return (
      <Chip
        label="✅ Available"
        size="small"
        sx={{ bgcolor: '#4CAF50', color: '#000', fontSize: '0.75rem' }}
      />
    );
  };

  const calculateReturn = (series: Series): number => {
    return series.principalAmount > 0
      ? ((series.currentNAV - series.principalAmount) / series.principalAmount) * 100
      : 0;
  };

  const getHWMStatus = (series: Series): React.ReactElement => {
    const isAboveHWM = series.currentNAV > series.highWaterMark;
    const percentFromHWM = series.highWaterMark > 0
      ? ((series.currentNAV - series.highWaterMark) / series.highWaterMark) * 100
      : 0;

    if (isAboveHWM) {
      return (
        <Tooltip title={`+${percentFromHWM.toFixed(1)}% above HWM`}>
          <span style={{ color: '#4CAF50' }}>✓ Above</span>
        </Tooltip>
      );
    } else {
      return (
        <Tooltip title={`${percentFromHWM.toFixed(1)}% below HWM`}>
          <span style={{ color: '#FF9800' }}>⚠ Underwater</span>
        </Tooltip>
      );
    }
  };

  if (activeSeries.length === 0) {
    return (
      <Card>
        <CardContent>
          <Typography sx={{ textAlign: 'center', py: 4, color: '#999' }}>
            No active series. Create a series to get started.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom sx={{ color: '#FFD700', mb: 3 }}>
          All Investment Series ({activeSeries.length})
        </Typography>

        <Box sx={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #333' }}>
                <th style={{ textAlign: 'left', padding: '12px', color: '#FFD700' }}>Lender</th>
                <th style={{ textAlign: 'left', padding: '12px', color: '#FFD700' }}>Issue Date</th>
                <th style={{ textAlign: 'right', padding: '12px', color: '#FFD700' }}>Principal</th>
                <th style={{ textAlign: 'right', padding: '12px', color: '#FFD700' }}>Current NAV</th>
                <th style={{ textAlign: 'right', padding: '12px', color: '#FFD700' }}>High Water Mark</th>
                <th style={{ textAlign: 'center', padding: '12px', color: '#FFD700' }}>HWM Status</th>
                <th style={{ textAlign: 'right', padding: '12px', color: '#FFD700' }}>Return</th>
                <th style={{ textAlign: 'center', padding: '12px', color: '#FFD700' }}>Lock-up</th>
                <th style={{ textAlign: 'center', padding: '12px', color: '#FFD700' }}>Status</th>
                <th style={{ textAlign: 'center', padding: '12px', color: '#FFD700' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {activeSeries.map((s) => {
                const returnPct = calculateReturn(s);
                const returnColor = returnPct >= 0 ? '#4CAF50' : '#F44336';

                return (
                  <tr key={s.id} style={{ borderBottom: '1px solid #333' }}>
                    <td style={{ padding: '12px' }}>{getLenderName(s.lenderId)}</td>
                    <td style={{ padding: '12px' }}>
                      {format(new Date(s.issueDate), 'dd MMM yyyy')}
                    </td>
                    <td style={{ padding: '12px', textAlign: 'right' }}>
                      {formatCurrency(s.principalAmount)}
                    </td>
                    <td style={{ padding: '12px', textAlign: 'right', fontWeight: 600 }}>
                      {formatCurrency(s.currentNAV)}
                    </td>
                    <td style={{ padding: '12px', textAlign: 'right', color: '#FFD700' }}>
                      {formatCurrency(s.highWaterMark)}
                    </td>
                    <td style={{ padding: '12px', textAlign: 'center' }}>
                      {getHWMStatus(s)}
                    </td>
                    <td style={{ padding: '12px', textAlign: 'right', color: returnColor, fontWeight: 600 }}>
                      {formatPercentage(returnPct / 100)}
                    </td>
                    <td style={{ padding: '12px', textAlign: 'center' }}>
                      {getLockupStatus(s)}
                    </td>
                    <td style={{ padding: '12px', textAlign: 'center' }}>
                      {getStatusChip(s.status)}
                    </td>
                    <td style={{ padding: '12px', textAlign: 'center' }}>
                      {onViewDetails && (
                        <IconButton
                          size="small"
                          onClick={() => onViewDetails(s)}
                          sx={{ color: '#FFD700', mr: 1 }}
                        >
                          <Visibility fontSize="small" />
                        </IconButton>
                      )}
                      {onDelete && (
                        <IconButton
                          size="small"
                          onClick={() => onDelete(s.id, `${getLenderName(s.lenderId)} - ${format(new Date(s.issueDate), 'MMM yyyy')}`)}
                          sx={{ color: '#ff4444' }}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Box>

        {/* Summary Row */}
        <Box sx={{ mt: 3, pt: 3, borderTop: '2px solid #FFD700' }}>
          <Typography variant="body2" sx={{ color: '#999' }}>
            Total Series: {activeSeries.length} | 
            Total Principal: {formatCurrency(activeSeries.reduce((sum, s) => sum + s.principalAmount, 0))} | 
            Total NAV: {formatCurrency(activeSeries.reduce((sum, s) => sum + s.currentNAV, 0))}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

export default SeriesListTable;
